import React, { useState, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';
import EidCard from './EidCard';
import Navbar from './Navbar';
import Footer from './Footer';
import './CardEditor.css';

const FONTS = ['Cairo', 'Amiri', 'Noto Naskh Arabic', 'Tajawal', 'Mada', 'Lateef'];

export default function CardEditor({ card, settings, onSave, onBack, user, onLogout }) {
  const [name, setName] = useState(settings.name);
  const [fontFamily, setFontFamily] = useState(settings.fontFamily);
  const [nameColor, setNameColor] = useState(settings.nameColor);
  const [fontSize, setFontSize] = useState(settings.fontSize);
  const [sharing, setSharing] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const cardRef = useRef(null);

  const getBlob = useCallback(async () => {
    if (!cardRef.current) return null;
    const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true });
    const res = await fetch(dataUrl);
    return res.blob();
  }, []);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await getBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eid-card-${card.id}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  }, [card.id, getBlob]);

  const handleShare = useCallback(async (platform) => {
    setSharing(platform);
    try {
      const blob = await getBlob();
      if (!blob) return;
      const file = new File([blob], 'eid-card.png', { type: 'image/png' });
      const txt = encodeURIComponent(`عيد الفطر السعيد  تهانينا لـ ${name} #عيد_مبارك`);

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'عيد الفطر السعيد', text: `تهانينا لـ ${name}` });
        return;
      }
      // Desktop fallback: download image then open platform
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'eid-card.png';
      a.click();
      URL.revokeObjectURL(url);
      setTimeout(() => {
        if (platform === 'whatsapp') window.open(`https://wa.me/?text=${txt}`, '_blank', 'noopener');
        else window.open(`https://twitter.com/intent/tweet?text=${txt}`, '_blank', 'noopener');
      }, 400);
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    } finally {
      setSharing(null);
    }
  }, [name, getBlob]);

  return (
    <div className="ceditor-page">
      <Navbar user={user} onLogout={onLogout} />
      <div className="ceditor-toolbar">
        <button className="btn-back" onClick={onBack}>→ رجوع  </button>
        <h2 className="ceditor-title">تعديل البطاقة</h2>
        <button
          className="btn-save"
          onClick={() => onSave({ name, fontFamily, nameColor, fontSize })}
        >
          حفظ التغييرات ✓
        </button>
      </div>

      <main className="ceditor-main">
        {/* ── Controls ── */}
        <div className="ceditor-controls">
          <div className="ce-field">
            <label>الاسم على البطاقة</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="اكتب اسمك هنا"
              dir="rtl"
              autoFocus
            />
          </div>

          <div className="ce-field">
            <label>نوع الخط</label>
            <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
              {FONTS.map((f) => (
                <option key={f} value={f} style={{ fontFamily: f }}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          <div className="ce-field">
            <label>حجم الخط — <strong>{fontSize}px</strong></label>
            <input
              type="range"
              min="14"
              max="52"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="ce-slider"
            />
          </div>

          <div className="ce-field">
            <label>لون الاسم</label>
            <div className="ce-color-row">
              <input
                type="color"
                value={nameColor}
                onChange={(e) => setNameColor(e.target.value)}
                className="ce-color-input"
              />
              <span className="ce-color-val">{nameColor}</span>
              <button
                className="btn-reset-clr"
                onClick={() => setNameColor(card.defaultColor)}
                title="إعادة اللون الافتراضي"
              >
                ↺
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="ce-actions">
            <button className="ce-btn-dl" onClick={handleDownload} disabled={downloading}>
              {downloading ? 'جارٍ التحميل…' : '⬇ تحميل البطاقة'}
            </button>
          </div>

          <div className="ce-share-row">
            <p className="ce-share-label">مشاركة البطاقة المعدَّلة:</p>
            <button
              className="ce-share-btn wa"
              onClick={() => handleShare('whatsapp')}
              disabled={sharing !== null}
            >
              {sharing === 'whatsapp' ? '…' : 'WhatsApp'}
            </button>
            <button
              className="ce-share-btn tw"
              onClick={() => handleShare('twitter')}
              disabled={sharing !== null}
            >
              {sharing === 'twitter' ? '…' : 'X / Twitter'}
            </button>
          </div>
        </div>

        {/* ── Live card preview ── */}
        <div className="ceditor-preview">
          <EidCard
            ref={cardRef}
            card={card}
            name={name}
            nameColor={nameColor}
            fontSize={fontSize}
            fontFamily={fontFamily}
            namePos={card.namePos}
            noShadow={card.id === 4}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
